import React, { useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";

/**
 * EgoNetworkPlot
 * ──────────────
 * Interactive 1-hop ego network around a selected residue, rendered with
 * Plotly. Each neighbor is laid out on a radial circle; the center is the
 * selected residue. Edges between the center and SPM neighbors (as well as
 * any passed-in `spmEdges`) are drawn in an accent color.
 *
 * Props
 *   center      String — selected residue (label on the center node).
 *   neighbors   Array<{
 *                  position,          // string — required
 *                  distance,          // number — required, Å
 *                  betweenness,       // number — optional
 *                  isHub,             // boolean — optional (SPM neighbor flag)
 *                  annotations,       // object — optional, free-form tag data
 *               }>
 *   spmEdges    (optional) Array<[a, b]> — residue pairs flagged as SPM edges
 *               (only affects edge color when one endpoint is the center).
 *   onClose     (optional) () => void — render an "X Close" in the header.
 *   title       (optional) override for the card title.
 *   percentile  (optional) number — used for the SPM hub legend label.
 */
const EgoNetworkPlot = ({
    center,
    neighbors = [],
    spmEdges = [],
    onClose,
    title,
    percentile = 10,
}) => {

    const [filters, setFilters] = useState({
        selected: true,
        amPathogenic: true,
        amAmbiguous: true,
        amBenign: true,
        amUnknown: true,
        spmEdges: true,
    });

    // AlphaMissense color palette — single source of truth
    const AM_PALETTE = {
        likely_pathogenic: { color: "#d64545", label: "Likely pathogenic" },
        ambiguous:         { color: "#e0a82a", label: "Ambiguous" },
        likely_benign:     { color: "#4a90c5", label: "Likely benign" },
        unknown:           { color: "#9aa0a6", label: "Unknown / N.A." },
    };

    const amBucket = (cls) => {
        const c = String(cls || "").toLowerCase();
        if (c === "likely_pathogenic") return "likely_pathogenic";
        if (c === "likely_benign")     return "likely_benign";
        if (c === "ambiguous")         return "ambiguous";
        return "unknown";
    };

    const spmEdgeSet = useMemo(() => {
        const s = new Set();
        spmEdges.forEach(([a, b]) => {
            const k = [String(a), String(b)].sort().join("|");
            s.add(k);
        });
        return s;
    }, [spmEdges]);

    const renderTags = (summary) => {
    console.log(summary)
    if (!summary) return <span className="text-secondary">-</span>;

    return (
        <div className="flex gap-1 justify-content-center">
            {summary.is_functional && (
                <>
                    <Tooltip target=".is_functional" />
                    <i
                        className="is_functional pi pi-cog p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Functional Significance"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_clinvar && (
                <>
                    <Tooltip target=".has_clinvar" />
                    <i
                        className="has_clinvar pi pi-shield p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has ClinVar variant"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_intra_contact && (
                <>
                    <Tooltip target=".has_intra_contact" />
                    <i
                        className="has_intra_contact pi pi-globe p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has Intra-protein Contact"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_af_intra_contact && (
                <>
                    <Tooltip target=".has_af_intra_contact" />
                    <i
                        className="has_af_intra_contact pi pi-star p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has AlphaFold Intra-protein Contact"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_inter_contact && (
                <>
                    <Tooltip target=".has_inter_contact" />
                    <i
                        className="has_inter_contact pi pi-sitemap p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has Inter-protein Interaction"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_af_inter_contact && (
                <>
                    <Tooltip target=".has_af_inter_contact" />
                    <i
                        className="has_af_inter_contact pi pi-star-fill p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has AlphaFold Inter-protein Contact"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
            {summary.has_ligand && (
                <>
                    <Tooltip target=".has_ligand" />
                    <i
                        className="has_ligand pi pi-ethereum p-1"
                        style={{ color: "#708090" }}
                        data-pr-tooltip="Has Ligand Contact"
                        data-pr-position="right"
                        data-pr-at="right+5 top"
                        data-pr-my="left center-2"
                    />
                </>
            )}
        </div>
    );
};

    // Build radial layout
    const layoutData = useMemo(() => {
        const n = neighbors.length;
        if (n === 0) return { nodes: [], edges: [] };

        const radius = 1;
        const nodes = neighbors.map((nb, i) => {
            const angle = (2 * Math.PI * i) / n - Math.PI / 2;
            return {
                ...nb,
                x: radius * Math.cos(angle),
                y: radius * Math.sin(angle),
            };
        });

        const edges = nodes.map((nb) => {
            const edgeKey = [String(center), String(nb.position)].sort().join("|");
            const isSpmEdge = spmEdgeSet.has(edgeKey) || (nb.isHub && filters.spmEdges);
            return { ...nb, isSpmEdge };
        });

        return { nodes, edges };
    }, [neighbors, center, spmEdgeSet, filters.spmEdges]);

    const plotData = useMemo(() => {
        const traces = [];
        const { nodes, edges } = layoutData;

        // Edges first (so nodes render on top)
        const regularEdgeX = [];
        const regularEdgeY = [];
        const spmEdgeX = [];
        const spmEdgeY = [];

        const isBucketVisible = (b) => {
            if (b === "likely_pathogenic") return filters.amPathogenic;
            if (b === "ambiguous")         return filters.amAmbiguous;
            if (b === "likely_benign")     return filters.amBenign;
            return filters.amUnknown;
        };

        edges.forEach((e) => {
            if (!isBucketVisible(amBucket(e.amClass))) return;

            const showAsSpm = e.isSpmEdge && filters.spmEdges;
            if (showAsSpm) {
                spmEdgeX.push(0, e.x, null);
                spmEdgeY.push(0, e.y, null);
            } else {
                regularEdgeX.push(0, e.x, null);
                regularEdgeY.push(0, e.y, null);
            }
        });

        if (regularEdgeX.length > 0) {
            traces.push({
                x: regularEdgeX,
                y: regularEdgeY,
                mode: "lines",
                type: "scatter",
                line: { color: "rgba(130, 130, 130, 0.35)", width: 1 },
                hoverinfo: "skip",
                showlegend: false,
            });
        }

        if (spmEdgeX.length > 0) {
            traces.push({
                x: spmEdgeX,
                y: spmEdgeY,
                mode: "lines",
                type: "scatter",
                line: { color: "#1e9e8a", width: 2.5 },
                hoverinfo: "skip",
                name: "SPM edge",
                showlegend: false,
            });
        }

        // Group nodes by AlphaMissense class — each bucket gets its own legend entry.
        // SPM hub status becomes a secondary visual cue (larger marker + white halo).
        const nodesByBucket = {
            likely_pathogenic: [],
            ambiguous: [],
            likely_benign: [],
            unknown: [],
        };
        nodes.forEach((n) => {
            nodesByBucket[amBucket(n.amClass)].push(n);
        });

        const buildNodeTrace = (arr, bucketKey) => {
            if (arr.length === 0) return null;
            const palette = AM_PALETTE[bucketKey];
            return {
                x: arr.map((n) => n.x),
                y: arr.map((n) => n.y),
                text: arr.map((n) => n.position),
                customdata: arr.map((n) => [
                    n.distance != null ? `${Number(n.distance).toFixed(2)} Å` : "-",
                    n.betweenness != null ? Number(n.betweenness).toFixed(2) : "-",
                    palette.label,
                    n.isHub ? "Yes" : "No",
                ]),
                hovertemplate:
                    "<b>%{text}</b><br>Distance: %{customdata[0]}<br>" +
                    "Betweenness: %{customdata[1]}<br>" +
                    "AlphaMissense: %{customdata[2]}<br>" +
                    "SPM hub: %{customdata[3]}<extra></extra>",
                mode: "markers+text",
                type: "scatter",
                name: palette.label,
                textposition: "bottom center",
                textfont: { size: 11 },
                marker: {
                    size: arr.map((n) => 16 + (n.isHub ? 8 : 0)),
                    color: palette.color,
                    line: {
                        color: arr.map((n) => (n.isHub ? "#1e9e8a" : "#ffffff")),
                        width: arr.map((n) => (n.isHub ? 3 : 2)),
                    },
                    symbol: "circle",
                },
            };
        };

        const bucketFilter = {
            likely_pathogenic: filters.amPathogenic,
            ambiguous:         filters.amAmbiguous,
            likely_benign:     filters.amBenign,
            unknown:           filters.amUnknown,
        };

        ["likely_pathogenic", "ambiguous", "likely_benign", "unknown"].forEach((b) => {
            if (!bucketFilter[b]) return;
            const t = buildNodeTrace(nodesByBucket[b], b);
            if (t) traces.push(t);
        });

        // Center node
        if (filters.selected) {
            traces.push({
                x: [0],
                y: [0],
                text: [String(center)],
                mode: "markers+text",
                type: "scatter",
                name: "Selected",
                textposition: "top center",
                textfont: { size: 13, color: "#111", family: "sans-serif" },
                marker: {
                    size: 30,
                    color: "#1e9e8a",
                    line: { color: "#ffffff", width: 3 },
                    symbol: "circle",
                },
                hovertemplate: `<b>${center}</b><br>Selected<extra></extra>`,
            });
        }

        return traces;
    }, [layoutData, filters, center, percentile]);

    const plotLayout = useMemo(
        () => ({
            autosize: true,
            showlegend: true,
            legend: {
                orientation: "h",
                y: -0.08,
                x: 0.5,
                xanchor: "center",
            },
            margin: { t: 20, b: 40, l: 20, r: 20 },
            xaxis: {
                visible: false,
                range: [-1.4, 1.4],
                scaleanchor: "y",
                scaleratio: 1,
            },
            yaxis: {
                visible: false,
                range: [-1.4, 1.4],
            },
            plot_bgcolor: "rgba(0,0,0,0)",
            paper_bgcolor: "rgba(0,0,0,0)",
            hovermode: "closest",
        }),
        []
    );

    // Neighbor table
    const distBody = (r) =>
        r.distance != null ? `${Number(r.distance).toFixed(2)} Å` : "-";
    const btwBody = (r) =>
        r.betweenness != null ? Number(r.betweenness).toFixed(2) : "-";
    const spmBody = (r) =>
        r.isHub ? (
            <Tag value="Yes" severity="warning" rounded icon="pi pi-star-fill" />
        ) : (
            <Tag value="No" severity="secondary" rounded />
        );
    const funcBody = (r) => {
        if (r.annotations?.has_ligand)
            return <Tag value="Ligand binding" severity="info" />;
        if (r.annotations?.is_functional)
            return <Tag value="Functional" severity="success" />;
        return <span className="text-secondary">-</span>;
    };

    const amTag = (r) => {
        const b = amBucket(r.amClass);
        const palette = AM_PALETTE[b];
        if (b === "unknown") {
            return <span className="text-secondary small">N.A.</span>;
        }
        return (
            <Tag
                value={palette.label}
                style={{ backgroundColor: palette.color, color: "#fff" }}
                rounded
            />
        );
    };

    return (
        <div className="card p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">
                        {title || "Residue neighborhood"}{" "}
                        <span className="text-secondary fw-normal fs-2">
                            | {center}
                        </span>
                    </h4>
                    <p className="text-secondary small mb-0 mt-1">
                        Structural contacts and SPM edges for residue {center}
                    </p>
                </div>
                {onClose && (
                    <Button
                        icon="pi pi-times"
                        label="Close"
                        text
                        severity="secondary"
                        onClick={onClose}
                    />
                )}
            </div>

            {/* Filter row */}
            <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
                <FilterCheck
                    id="f-selected"
                    label="Selected"
                    color="#1e9e8a"
                    checked={filters.selected}
                    onChange={(v) => setFilters((s) => ({ ...s, selected: v }))}
                />
                <FilterCheck
                    id="f-am-path"
                    label="Likely pathogenic"
                    color="#d64545"
                    checked={filters.amPathogenic}
                    onChange={(v) => setFilters((s) => ({ ...s, amPathogenic: v }))}
                />
                <FilterCheck
                    id="f-am-amb"
                    label="Ambiguous"
                    color="#e0a82a"
                    checked={filters.amAmbiguous}
                    onChange={(v) => setFilters((s) => ({ ...s, amAmbiguous: v }))}
                />
                <FilterCheck
                    id="f-am-ben"
                    label="Likely benign"
                    color="#4a90c5"
                    checked={filters.amBenign}
                    onChange={(v) => setFilters((s) => ({ ...s, amBenign: v }))}
                />
                <FilterCheck
                    id="f-am-unk"
                    label="Unknown / N.A."
                    color="#9aa0a6"
                    checked={filters.amUnknown}
                    onChange={(v) => setFilters((s) => ({ ...s, amUnknown: v }))}
                />
                <FilterCheck
                    id="f-spm-edges"
                    label="SPM edges"
                    color="#1e9e8a"
                    line
                    checked={filters.spmEdges}
                    onChange={(v) => setFilters((s) => ({ ...s, spmEdges: v }))}
                />
                <span className="small text-secondary ms-auto">
                    <i className="pi pi-info-circle me-1" />
                    Larger nodes with teal halo are SPM hubs (top {percentile}%)
                </span>
            </div>

            <div style={{ width: "100%", height: 380 }}>
                <Plot
                    data={plotData}
                    layout={plotLayout}
                    useResizeHandler
                    style={{ width: "100%", height: "100%" }}
                    config={{ displaylogo: false, responsive: true }}
                />
            </div>

            {/* Neighbor table */}
            <div className="mt-3">
                <DataTable
                    value={neighbors}
                    size="small"
                    stripedRows
                    emptyMessage="No neighbors found."
                    paginator={neighbors.length > 10}
                    rows={10}
                >
                    <Column
                        field="position"
                        header="Neighbor"
                        body={(r) => (
                            <span className="fw-bold text-primary">{r.position}</span>
                        )}
                        sortable
                    />
                    <Column
                        field="distance"
                        header="Dist."
                        sortable
                        body={distBody}
                    />
                    <Column
                        field="betweenness"
                        header="Edge btw."
                        sortable
                        body={btwBody}
                    />
                    <Column field="isHub" header="SPM" sortable body={spmBody} />
                    <Column
                        field="amClass"
                        header="AlphaMissense"
                        sortable
                        body={amTag}
                    />
                    <Column
                        header="Functional tag"
                        body={(r) => renderTags(r.annotations?.summary)}
                    />
                </DataTable>
            </div>
        </div>
    );
};


const FilterCheck = ({ id, label, color, checked, onChange, line }) => (
    <div className="d-flex align-items-center gap-2">
        <Checkbox
            inputId={id}
            checked={checked}
            onChange={(e) => onChange(e.checked)}
        />
        <label
            htmlFor={id}
            className="d-flex align-items-center gap-2 mb-0"
            style={{ cursor: "pointer" }}
        >
            {line ? (
                <span
                    style={{
                        display: "inline-block",
                        width: 18,
                        height: 2,
                        background: color,
                        borderRadius: 2,
                    }}
                />
            ) : (
                <span
                    style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        background: color,
                        borderRadius: "50%",
                        border: "2px solid white",
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
                    }}
                />
            )}
            <span className="small">{label}</span>
        </label>
    </div>
);

export default EgoNetworkPlot;
